import { useEffect, useState } from 'react';

import { Radio, AlertBox } from '@iwoca/orion';
import classnames from 'classnames';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { connect } from 'react-redux';

import BankStatementOpenBanking from './BankStatementOpenBanking';
import messages from './BusinessBankStatement.messages';
import styles from './BusinessBankStatement.module.css';
import BusinessBankStatementManual from './BusinessBankstatementManual/BusinessBankStatementManual';
import BusinessBankStatementTrueLayer from './BusinessBankStatementTrueLayer/BusinessBankStatementTrueLayer';
import TrueLayerAlertBox from './BusinessBankStatementTrueLayer/TrueLayerAlertBox';
import { TTrueLayerBank } from './BusinessBankStatementTrueLayer/TrueLayerModal';
import { TAlertStatus } from './hooks/useTrueLayerRedirected';
import {
    getCountryCode,
    getSuggestedAmount,
    getTrueLayerBanks
} from '../../../lendingAPI/lendingAPISelector';
import { RootState } from '../../../reducers';
import { getSplitTestValue } from '../../../utils/selectors';
import MissingRequiredDocWarning from '../MissingRequiredDocWarning/MissingRequiredDocWarning';
import { useDocPresentStatus } from '../MissingRequiredDocWarning/MissingRequiredDocWarning.hook';
import thstyles from '../TradingHistory.module.css';
import {
    getHasUploadedDocumentByType,
    getTrueLayerLinkedBankAccounts
} from '../TradingHistory.selectors';
import { TBankStatementResolutionMethods } from '../types';

type TAccountLinkType =
    | 'manual_upload'
    | 'bank_account'
    | 'de_bank_and_manual_upload';

const mapStateToProps = (state: RootState) => {
    return {
        country: getCountryCode(state),
        missingDocs: state?.tradingHistory?.missing,
        hasUploadedBankStatments: getHasUploadedDocumentByType(
            state,
            'bank_statement'
        ),
        trueLayerBanks: getTrueLayerBanks(state),
        trueLayerLinkedBankAccount: getTrueLayerLinkedBankAccounts(state),
        isInSplitOpenBankingPreference: getSplitTestValue(
            state,
            'open_banking_preference'
        ),
        isInLinkOBBranch:
            (getSplitTestValue(state, 'de_auto_bs_required_test_2024') ===
                'OB_REQUIRED' &&
                getSuggestedAmount(state) <= 15000) ||
            getSplitTestValue(
                state,
                'de_manual_lending_open_banking_required_test_2024_q4'
            ) === 'OB_REQUIRED'
    };
};

const BusinessBankStatement = ({
    country,
    quantifier,
    isOptional,
    missingDocs,
    intl,
    hasUploadedBankStatments,
    trueLayerBanks,
    trueLayerLinkedBankAccount,
    isInSplitOpenBankingPreference,
    isInLinkOBBranch
}: {
    country: 'uk' | 'de';
    quantifier: number;
    isOptional: boolean;
    missingDocs: [];
    hasUploadedBankStatments: boolean;
    trueLayerBanks: TTrueLayerBank[];
    trueLayerLinkedBankAccount: TBankStatementResolutionMethods | null;
    isInSplitOpenBankingPreference: boolean;
    isInLinkOBBranch: boolean;
} & WrappedComponentProps) => {
    const [linkedBankAccount, setLinkedBankAccount] =
        useState<TTrueLayerBank | null>(null);

    const isBankStatementDocPresent = useDocPresentStatus(
        missingDocs,
        country === 'de' ? 'kontoauszuege_hochladen' : 'bank_statement'
    );

    let bankStatementTitle;
    const [accountLinkType, setAccountLinkType] = useState<TAccountLinkType>(
        hasUploadedBankStatments
            ? 'manual_upload'
            : isInLinkOBBranch
            ? 'de_bank_and_manual_upload'
            : 'bank_account'
    );

    const AccountLinkDetails = [
        {
            //@ts_ignore
            header: intl.formatMessage(messages.linkBankAccount),
            value: 'bank_account',
            country: ['uk', 'de']
        },
        {
            header: intl.formatMessage(messages.uploadPDF),
            value: 'manual_upload',
            country: ['uk', 'de']
        }
    ];

    const [trueLayerAlertStatus, setTrueLayerAlertStatus] =
        useState<TAlertStatus>(null);

    useEffect(() => {
        getLinkedBankAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLinkedBankAccount = () => {
        if (!trueLayerBanks || !trueLayerLinkedBankAccount) return;

        const bankAccount = trueLayerBanks.filter((bank) => {
            return (
                bank.bank_id === trueLayerLinkedBankAccount.online_account__bank
            );
        });

        setLinkedBankAccount(bankAccount[0]);
    };

    const handleChange = (value: TAccountLinkType) => {
        setAccountLinkType(value);
    };

    const renderAccountLinkTypeDetails = () => {
        switch (accountLinkType) {
            case 'bank_account':
                return (
                    <BusinessBankStatementTrueLayer
                        setAlertStatus={setTrueLayerAlertStatus}
                        bankList={trueLayerBanks}
                    />
                );
            case 'manual_upload':
                return <BusinessBankStatementManual isOptional={isOptional} />;
            case 'de_bank_and_manual_upload':
                //Show both options in DE test.
                return (
                    <>
                        <BusinessBankStatementTrueLayer
                            setAlertStatus={setTrueLayerAlertStatus}
                            bankList={trueLayerBanks}
                        />
                        <BusinessBankStatementManual
                            isOptional={isOptional}
                            isInLinkOBBranch={isInLinkOBBranch}
                        />
                    </>
                );
        }
    };

    if (quantifier > 1) {
        bankStatementTitle = (
            <div className={thstyles.headerSection}>
                <h4 className={thstyles.title}>
                    <FormattedMessage
                        {...messages.title}
                        values={{ quantifier: quantifier }}
                    />
                </h4>
                {isBankStatementDocPresent && (
                    <MissingRequiredDocWarning dataTestId="bankstatement-warning-required" />
                )}
            </div>
        );
    } else {
        bankStatementTitle = (
            <div className={thstyles.headerSection}>
                <h4 className={thstyles.title}>
                    <FormattedMessage {...messages.title1Month} />
                </h4>
                {isBankStatementDocPresent && (
                    <MissingRequiredDocWarning dataTestId="bankstatement-warning-required" />
                )}
            </div>
        );
    }

    return (
        <>
            {bankStatementTitle}
            {isInSplitOpenBankingPreference && country === 'uk' ? (
                <BankStatementOpenBanking
                    setTrueLayerAlertStatus={setTrueLayerAlertStatus}
                    linkedBankAccount={linkedBankAccount}
                    isOptional={isOptional}
                    trueLayerAlertStatus={trueLayerAlertStatus}
                />
            ) : (
                <>
                    {isInLinkOBBranch ? null : (
                        <div className={styles.radioSectionContainer}>
                            <p className={styles.subtitle}>
                                <FormattedMessage {...messages.howToShare} />
                            </p>
                            {AccountLinkDetails.filter((ald) => {
                                const showForCountry =
                                    ald.country.includes(country);

                                const showForUserType = true;

                                return showForCountry && showForUserType;
                            }).map((accountLink) => (
                                <Radio
                                    key={accountLink.value}
                                    wrapperClassName={styles.radioSection}
                                    label={accountLink.header}
                                    onChange={() =>
                                        handleChange(
                                            accountLink.value as TAccountLinkType
                                        )
                                    }
                                    checked={
                                        accountLinkType === accountLink.value
                                    }
                                    frame={true}
                                />
                            ))}
                        </div>
                    )}
                    {renderAccountLinkTypeDetails()}

                    {trueLayerLinkedBankAccount && linkedBankAccount && (
                        <AlertBox
                            data-dd-privacy="mask"
                            className={classnames(
                                styles.bankAccountStatus,
                                'fs-exclude'
                            )}
                            variant="connect">
                            <img
                                className={styles.trueLayerBankLogo}
                                src={linkedBankAccount.logo_url}
                                alt={linkedBankAccount.bank_name}
                            />{' '}
                            <strong>
                                <FormattedMessage
                                    {...messages.linkedBankAccountStatus}
                                    values={{
                                        bankName: linkedBankAccount.bank_name
                                    }}
                                />
                            </strong>
                        </AlertBox>
                    )}

                    {trueLayerAlertStatus && (
                        <TrueLayerAlertBox status={trueLayerAlertStatus} />
                    )}
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps)(injectIntl(BusinessBankStatement));
